<template>
  <li
    class="mb-3 w-100 d-grid"
    :class="{ 'opacity-50': item._destroy }"
    style="grid-template-columns: 1fr auto;"
    @mouseleave="$emit('mouseleave')"
    @mouseover="$emit('mouseover')"
  >
    <div class="accordion-item" :class="{ 'border-danger': formItemHasErrors }">

      <!-- Accordion header -->
      <div class="accordion-header d-flex" :class="{ 'border-bottom': item.opened }"
        :id="`heading_${scopeToId(scope)}_${index}`">

        <!-- Position navigation -->
        <PositionControls :position="index + 1" :total-items="totalItems"
          @move-up="$emit('move-up')" @move-down="$emit('move-down')" />

        <!-- Indicators -->
        <FieldIndicators :item="item" :types="types" />

        <!-- Field summary -->
        <div
          class="w-100 d-flex flex-column align-items-start justify-content-between gap-2 p-3"
          :role="canItemOpen ? 'button' : 'listitem'"
          aria-expanded="false"
          :aria-label="item.opened
            ? $t('backend.candidacy.components.management_controls.collapse_edit')
            : $t('backend.candidacy.components.management_controls.open_edit')"
          @click.prevent.stop="toggleAccordion(`#body_${scopeToId(scope)}_${index}`)"
        >
          <div style="max-height: 1.5rem;" class="text-truncate">
            <h5 class="mb-1 text-wrap" :class="{ 'text-muted': !attributeLabel }">
              <AVIcon v-if="formItemHasErrors" icon="circle-exclamation" class="text-danger me-2" />
              <strong :class="{ 'text-danger': item._destroy }">
                {{ attributeLabel || `${$t("backend.candidacy.components.sortable_item.new")} ${humanize(item.item_type)}` }}
              </strong>
            </h5>
          </div>
          <p class="mb-0 small">
            <code v-if="!!attributeName" class="text-dark cursor-help" v-tooltip="`${$t('backend.candidacy.components.sortable_item.attribute_name')}: ${attributeName}`">
              {{ `${attributeName}` }}
            </code>
            <code v-if="item.type.includes('::FormItems::') && !!attributeName && !!item.purpose" class="text-dark"> • </code>
            <code v-if="!!item.purpose" class="text-dark cursor-help" v-tooltip="$t(`backend.candidacy_config.purposes.${item.purpose}_details`)">
              {{ $t(`backend.candidacy_config.purposes.${item.purpose}`).toLowerCase() }}
            </code>
          </p>
        </div>

        <!-- Field management buttons -->
        <ManagementControls
          :show-icons="item.showIcons"
          :target="`body_${scopeToId(scope)}_${index}`"
          :is-item-open="item.opened"
          :is-deleting="item._destroy"
          @toggle-delete="$emit('toggle-delete')"
          @toggle-new-options="$emit('toggle-new-options')"
          @toggle-accordion="$emit('toggle-accordion', item)"
        />
      </div>

      <!-- Accordion body -->
      <div
        :id="`body_${scopeToId(scope)}_${index}`"
        class="accordion-collapse collapse"
        :aria-labelledby="`heading_${scopeToId(scope)}_${index}`"
        data-bs-parent="#builder_accordion"
      >
        <div class="accordion-body" :class="{ 'p-0': item.type === 'Candidacy::PageBlocks::RichTextArea' }">
          <slot :item="item" :index="index" />
        </div>
      </div>
    </div>
  </li>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { EveryBuildableItem, PropType, ItemType } from "@/types";
import { scopeToId, humanize } from "@/lib/candidacy/form_builder/utils";
import PositionControls from "./PositionControls.vue";
import ManagementControls from "./ManagementControls.vue";
import FieldIndicators from "./FieldIndicators.vue";
import { Collapse } from "@assemblyvoting/electa-ui";

export default defineComponent({
  name: "SortableItem",
  components: {
    PositionControls,
    ManagementControls,
    FieldIndicators,
  },
  emits: ["mouseover", "mouseleave", "move-up", "move-down", "toggle-delete", "toggle-new-options", "toggle-accordion"],
  props: {
    item: {
      type: Object as PropType<EveryBuildableItem>,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    scope: {
      type: Array as PropType<string[]>,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    types: {
      type: Array as PropType<ItemType[]>,
      required: true,
    },
  },
  data() {
    return {
      toggling: false,
    };
  },
  computed: {
    attributeName() {
      if (this.item.item_type === "divider") return "divider";
      if (this.item.item_type === "information") return "information";
      if (this.item.type.includes("::PageBlocks::")) return this.item.item_type;
      return this.item.attribute_name;
    },
    attributeLabel() {
      if (this.item.item_type === "divider") return this.$t("backend.candidacy.components.sortable_item.attribute_label.divider"); // Dividers never have content

      // For the PageBuilder we usually want to see the content
      if (this.item.type.includes("::PageBlocks::")) {
        if (this.item.item_type === "image") return this.$t("backend.candidacy.components.sortable_item.attribute_label.image");
        if (this.item.item_type === "jumbotron")
          return this.htmlToPlainText(this.item.data?.primary_text_content?.[this.$i18n.locale]);
        return this.htmlToPlainText(this.item.data?.localised_wysiwyg?.[this.$i18n.locale]);
      }

      // For the FormBuilder we want to display the item label
      if (this.item.type.includes("::FormItems::")) {
        if (this.item.item_type === "information") // But if it's an info item just show the content
          return this.htmlToPlainText(this.item.config?.[this.$i18n.locale]);
        return this.item.label?.[this.$i18n.locale] || null;
      }
    },
    formItemHasErrors() {
      if (!this.item.errors) return false; 
      return !!Object.keys(this.item.errors).length;
    },
    canItemOpen() {
      return !this.item._destroy && this.item.item_type !== "divider";
    },
  },
  methods: {
    scopeToId,
    humanize,
    htmlToPlainText(htmlContent: String) {
      if (!htmlContent) return null;
      return htmlContent.replace(/<[^>]*>/g, " ");
    },
    async toggleAccordion(id: string) {
      if (!this.canItemOpen || this.toggling) return;
      this.toggling = true;
      const currentItem = document.querySelector(id);
      this.item.opened 
        ? Collapse.getOrCreateInstance(currentItem).hide()
        : Collapse.getOrCreateInstance(currentItem).show()
      await this.$emit('toggle-accordion', this.item);
      setTimeout(() => this.toggling = false, 400); 
    },
  },
});
</script>
